import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getCustodialWalletForUserId } from "../../core/db/custodial";
import { getMartianUser, updateMartianUser} from "../../core/db/user";
import { UserState, UserError, FetchUserParams, MartianUser, FetchUserCustodialDetailsParams } from "./types";
import { SupabaseClient } from "@supabase/supabase-js";

const initialState: UserState = {
    martianUser: {
        id: '',
        martianName: '',
        avatarUrl: '',
        hasSetup: false,
        hasCustodialWallet: false,
        walletAddress: " ",
        email: " ",

    },
    loggedIn: false,
    custodialWalletAddress: undefined,
    loading: false, 
    error: UserError.NONE,
}

export const fetchUser = createAsyncThunk('user/fetchuser', async (args: FetchUserParams) => {
    const user = await getMartianUser(args.supabase, args.userId);
    return user
})

// Define a type for the update parameters
interface UpdateUserParams {
    supabase: SupabaseClient;
    userId: string;
    walletAddress: string;
    hasCustodialWallet: boolean;
  }

  export const updateUser = createAsyncThunk(
    'user/updateUser',
    async (args: UpdateUserParams, { rejectWithValue }) => {
      try {
        const updated = await updateMartianUser(args.supabase, args.userId, args.walletAddress, args.hasCustodialWallet);
        if (!updated) {
          throw new Error('Failed to update user');
        }
        return { userId: args.userId, walletAddress: args.walletAddress, hasCustodialWallet: args.hasCustodialWallet };
      } catch (error) {
        // More robust error handling
        if (error instanceof Error) {
          return rejectWithValue(error.message);
        } else {
          return rejectWithValue('An unknown error occurred');
        }
      }
    }
  );

export const fetchUserCustodialDetails = createAsyncThunk('custodialHoldings/userCustodialWalletAddress',
    async (args: FetchUserCustodialDetailsParams) => {
    const walletAddress = await getCustodialWalletForUserId(args.supabase, args.userId);
    return walletAddress
})

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        loading: (state) => { state.loading = true },
        fetched: (state) => { state.loading = false },
        clear: (state) => { state = initialState },
    },
    extraReducers: (builder) => {
        // === Fetch User ===
        builder.addCase(fetchUser.pending, (state) => {
            console.log('PENDING');
            state.loading = true
        })
        builder.addCase(fetchUser.fulfilled, (state, action: PayloadAction<MartianUser>) => {
            console.log('FULFILLED');
            console.log("Redux Fulfilled User Data:", action.payload);
            state.martianUser = action.payload;
            state.loading = false;
            state.error = UserError.NONE;
        })
        builder.addCase(fetchUser.rejected, (state) => {
            console.log('REJECTED');
            state.error = UserError.LOADING_FAILED;
        })
        
        
        // === Fetch User Wallet ===
        builder.addCase(fetchUserCustodialDetails.pending, (state) => {
            console.log('PENDING');
            state.loading = true
        })
        builder.addCase(fetchUserCustodialDetails.fulfilled, (state, action: PayloadAction<string>) => {
            console.log('FULFILLED');
            state.custodialWalletAddress = action.payload;
            state.loading = false;
            state.error = UserError.NONE;
        })
        builder.addCase(fetchUserCustodialDetails.rejected, (state) => {
            console.log('REJECTED');
            state.error = UserError.LOADING_FAILED;
        })
        builder.addCase(updateUser.pending, (state) => {
            state.loading = true;
            state.error = UserError.NONE;
          });
          builder.addCase(updateUser.fulfilled, (state, action: PayloadAction<{ userId: string, walletAddress: string, hasCustodialWallet: boolean }>) => {
            // Assuming you want to update the state with the new information
            if (state.martianUser.id === action.payload.userId) {
              state.martianUser.walletAddress = action.payload.walletAddress;
              state.martianUser.hasCustodialWallet = action.payload.hasCustodialWallet;
            }
            state.loading = false;
            state.error = UserError.NONE;
          });
          builder.addCase(updateUser.rejected, (state, action) => {
            state.loading = false;
            state.error = UserError.NONE;
          });
    }
})

// Selectors
export const selectMartianUser = (state: UserState) => state.martianUser;
export const selectUserId = (state: UserState) => state.martianUser?.id;
export const userHasCustodialWallet = (state: UserState) => state.martianUser?.hasCustodialWallet;
export const selectUserErrors = (state: UserState) => state.error;
export const selectLoadingStatus = (state: UserState) => state.loading;

export default userSlice.reducer